import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type Language } from '@models/i18n/language.model';

const LOCALE_BY_LANGUAGE: Record<Language, string> = {
  en: 'en-AE',
  ar: 'ar-AE',
  ru: 'ru-RU',
};

type DateInput = Date | string;

const isSupportedLanguage = (language: string): language is Language => {
  return SUPPORTED_LANGUAGES.includes(language as Language);
};

const resolveLanguage = (language: string): Language => {
  return isSupportedLanguage(language) ? language : DEFAULT_LANGUAGE;
};

const isValidDate = (value: Date): boolean => {
  return !Number.isNaN(value.getTime());
};

const parseDateInput = (value: DateInput): Date => {
  if (value instanceof Date) {
    return value;
  }

  return new Date(value);
};

const formatDateValue = (
  value: DateInput,
  language: string,
  options: Intl.DateTimeFormatOptions,
): string => {
  const parsedValue = parseDateInput(value);

  if (!isValidDate(parsedValue)) {
    return value instanceof Date ? '' : value;
  }

  return new Intl.DateTimeFormat(toIntlLocale(language), options).format(parsedValue);
};

export const toIntlLocale = (language: string): string => {
  return LOCALE_BY_LANGUAGE[resolveLanguage(language)];
};

export const formatDate = (value: DateInput, language: string): string => {
  const parsedValue = parseDateInput(value);

  if (!isValidDate(parsedValue)) {
    return value instanceof Date ? '' : value;
  }

  const dateParts = new Intl.DateTimeFormat(toIntlLocale(language), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    weekday: 'long',
  }).formatToParts(parsedValue);

  const day = dateParts.find(
    (part: Intl.DateTimeFormatPart): boolean => part.type === 'day',
  )?.value;
  const month = dateParts.find(
    (part: Intl.DateTimeFormatPart): boolean => part.type === 'month',
  )?.value;
  const year = dateParts.find(
    (part: Intl.DateTimeFormatPart): boolean => part.type === 'year',
  )?.value;
  const weekday = dateParts.find(
    (part: Intl.DateTimeFormatPart): boolean => part.type === 'weekday',
  )?.value;

  if (!day || !month || !year || !weekday) {
    return new Intl.DateTimeFormat(toIntlLocale(language), {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      weekday: 'long',
    }).format(parsedValue);
  }

  return `${day} ${month} ${year}, ${weekday}`;
};

export const formatTime = (value: DateInput, language: string): string => {
  return formatDateValue(value, language, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

export const formatDateTime = (value: DateInput, language: string): string => {
  return formatDateValue(value, language, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

export interface DurationParts {
  hours: string;
  minutes: string;
}

export const toDurationParts = (totalMinutes: number): DurationParts => {
  const MINUTES_PER_HOUR = 60;
  const TIME_MINUTE_PADDING = 2;
  const safeMinutes = Math.max(totalMinutes, 0);
  const hours = Math.floor(safeMinutes / MINUTES_PER_HOUR)
    .toString()
    .padStart(TIME_MINUTE_PADDING, '0');
  const minutes = (safeMinutes % MINUTES_PER_HOUR).toString().padStart(TIME_MINUTE_PADDING, '0');

  return { hours, minutes };
};

export const getDurationInMinutes = (start: DateInput, end: DateInput): number | null => {
  const startDate = parseDateInput(start);
  const endDate = parseDateInput(end);

  if (!isValidDate(startDate) || !isValidDate(endDate)) {
    return null;
  }

  const MILLISECONDS_PER_MINUTE = 60_000;
  const durationInMinutes = Math.floor(
    (endDate.getTime() - startDate.getTime()) / MILLISECONDS_PER_MINUTE,
  );

  return Math.max(durationInMinutes, 0);
};

export const getHoursDifferenceFromNow = (value: DateInput): number | null => {
  const parsedValue = parseDateInput(value);

  if (!isValidDate(parsedValue)) {
    return null;
  }

  const now = new Date();
  const MILLISECONDS_PER_HOUR = 3_600_000;
  return Math.floor((parsedValue.getTime() - now.getTime()) / MILLISECONDS_PER_HOUR);
};

export const getIsoTimestamp = (): string => {
  return new Date().toISOString();
};

/**
 * Formats a date value to YYYY-MM-DD format
 * Used for APIS date fields (passport expiry, DOB, visa dates, etc.)
 *
 * @param value - Date input (Date object or ISO string)
 * @returns Formatted date as YYYY-MM-DD or empty string if invalid/null
 */
export const formatDateToYYYYMMDD = (value: DateInput | null): string => {
  if (!value) {
    return '';
  }

  const parsedValue = parseDateInput(value);

  if (!isValidDate(parsedValue)) {
    return '';
  }

  const DATE_COMPONENT_PADDING = 2;
  const MONTH_OFFSET = 1;
  const year = parsedValue.getFullYear();
  const month = String(parsedValue.getMonth() + MONTH_OFFSET).padStart(DATE_COMPONENT_PADDING, '0');
  const day = String(parsedValue.getDate()).padStart(DATE_COMPONENT_PADDING, '0');

  return `${year}-${month}-${day}`;
};
